import { supabase } from '@/lib/supabase'
import type {
  Nonconformance,
  NonconformanceInsert,
  NonconformanceUpdate,
  NonconformanceWithDetails,
  NonconformanceStats,
  NcrStatus,
  NcrSource,
  NcrSeverity,
  NcrCategory,
  NcrComment,
  NcrCommentInsert,
  NcrEvidence,
  NcrEvidenceInsert,
} from '@/types/qms'
import { profileService } from './profileService'

export const nonconformanceService = {
  // ============================================
  // NCR CRUD OPERATIONS
  // ============================================

  /**
   * Get all nonconformances for the current organization
   */
  async getNonconformances(): Promise<Nonconformance[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching nonconformances:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get nonconformances with related details (owner, product, supplier)
   */
  async getNonconformancesWithDetails(): Promise<NonconformanceWithDetails[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('nonconformances')
      .select(`
        *,
        owner:profiles!nonconformances_owner_id_fkey(
          id,
          full_name
        ),
        raised_by_profile:profiles!nonconformances_raised_by_fkey(
          id,
          full_name
        ),
        affected_product:products!nonconformances_affected_product_id_fkey(
          id,
          name,
          sku
        ),
        affected_supplier:suppliers!nonconformances_affected_supplier_id_fkey(
          id,
          name
        )
      `)
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching nonconformances with details:', error)
      return []
    }

    return (data ?? []) as NonconformanceWithDetails[]
  },

  /**
   * Get a single nonconformance by ID
   */
  async getNonconformance(id: string): Promise<Nonconformance | null> {
    if (!id?.trim()) return null

    const orgId = await profileService.getOrganizationId()
    if (!orgId) return null

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('id', id)
      .eq('organization_id', orgId)
      .single()

    if (error) {
      console.error('Error fetching nonconformance:', error)
      return null
    }

    return data
  },

  /**
   * Get a single nonconformance with all details
   */
  async getNonconformanceWithDetails(id: string): Promise<NonconformanceWithDetails | null> {
    if (!id?.trim()) return null
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return null

    const { data, error } = await supabase
      .from('nonconformances')
      .select(`
        *,
        owner:profiles!nonconformances_owner_id_fkey(
          id,
          full_name
        ),
        raised_by_profile:profiles!nonconformances_raised_by_fkey(
          id,
          full_name
        ),
        affected_product:products!nonconformances_affected_product_id_fkey(
          id,
          name,
          sku
        ),
        affected_supplier:suppliers!nonconformances_affected_supplier_id_fkey(
          id,
          name
        )
      `)
      .eq('id', id)
      .eq('organization_id', orgId)
      .single()

    if (error) {
      console.error('Error fetching nonconformance with details:', error)
      return null
    }

    return data as NonconformanceWithDetails
  },

  /**
   * Create a new nonconformance
   */
  async createNonconformance(ncr: NonconformanceInsert): Promise<Nonconformance | null> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('nonconformances')
      .insert({
        ...ncr,
        organization_id: orgId,
        created_by: user?.id,
        raised_by: ncr.raised_by ?? user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating nonconformance:', error)
      throw error
    }

    return data
  },

  /**
   * Update a nonconformance
   */
  async updateNonconformance(id: string, updates: NonconformanceUpdate): Promise<Nonconformance | null> {
    if (!id?.trim()) throw new Error('Invalid NCR ID')

    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { data, error } = await supabase
      .from('nonconformances')
      .update(updates)
      .eq('id', id)
      .eq('organization_id', orgId)
      .select()
      .single()

    if (error) {
      console.error('Error updating nonconformance:', error)
      throw error
    }

    return data
  },

  /**
   * Delete a nonconformance
   */
  async deleteNonconformance(id: string): Promise<boolean> {
    if (!id?.trim()) throw new Error('Invalid NCR ID')

    const orgId = await profileService.getOrganizationId()
    if (!orgId) throw new Error('No organization found')

    const { error } = await supabase
      .from('nonconformances')
      .delete()
      .eq('id', id)
      .eq('organization_id', orgId)

    if (error) {
      console.error('Error deleting nonconformance:', error)
      throw error
    }

    return true
  },

  // ============================================
  // FILTERED QUERIES
  // ============================================

  /**
   * Get nonconformances by status
   */
  async getNonconformancesByStatus(status: NcrStatus): Promise<Nonconformance[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('organization_id', orgId)
      .eq('status', status)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching NCRs by status:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get nonconformances by severity
   */
  async getNonconformancesBySeverity(severity: NcrSeverity): Promise<Nonconformance[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('organization_id', orgId)
      .eq('severity', severity)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching NCRs by severity:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get nonconformances by source
   */
  async getNonconformancesBySource(source: NcrSource): Promise<Nonconformance[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('organization_id', orgId)
      .eq('source', source)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching NCRs by source:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get nonconformances by category
   */
  async getNonconformancesByCategory(category: NcrCategory): Promise<Nonconformance[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('organization_id', orgId)
      .eq('category', category)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching NCRs by category:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get open NCRs (not closed or rejected)
   */
  async getOpenNonconformances(): Promise<Nonconformance[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('organization_id', orgId)
      .not('status', 'in', '("closed","rejected")')
      .order('severity', { ascending: false })
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching open NCRs:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get NCRs with overdue corrective actions
   */
  async getOverdueNonconformances(): Promise<Nonconformance[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const today = new Date().toISOString().split('T')[0]

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('organization_id', orgId)
      .not('status', 'in', '("closed","rejected")')
      .lt('corrective_action_due_date', today)
      .order('corrective_action_due_date', { ascending: true })

    if (error) {
      console.error('Error fetching overdue NCRs:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get NCRs pending effectiveness verification
   */
  async getPendingVerificationNonconformances(): Promise<Nonconformance[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('organization_id', orgId)
      .eq('status', 'pending_verification')
      .order('effectiveness_verification_date', { ascending: true })

    if (error) {
      console.error('Error fetching NCRs pending verification:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get NCRs assigned to a specific owner
   */
  async getNonconformancesByOwner(ownerId: string): Promise<Nonconformance[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('organization_id', orgId)
      .eq('owner_id', ownerId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching NCRs by owner:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get NCRs affecting a specific product
   */
  async getNonconformancesByProduct(productId: string): Promise<Nonconformance[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('organization_id', orgId)
      .eq('affected_product_id', productId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching NCRs by product:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Get NCRs affecting a specific supplier
   */
  async getNonconformancesBySupplier(supplierId: string): Promise<Nonconformance[]> {
    const orgId = await profileService.getOrganizationId()
    if (!orgId) return []

    const { data, error } = await supabase
      .from('nonconformances')
      .select('*')
      .eq('organization_id', orgId)
      .eq('affected_supplier_id', supplierId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching NCRs by supplier:', error)
      return []
    }

    return data ?? []
  },

  // ============================================
  // STATUS TRANSITIONS
  // ============================================

  /**
   * Start investigation
   */
  async startInvestigation(id: string): Promise<Nonconformance | null> {
    return this.updateNonconformance(id, { status: 'investigating' })
  },

  /**
   * Complete investigation with root cause
   */
  async completeInvestigation(
    id: string,
    rootCauseAnalysis: string,
    rootCauseCategory?: string,
    contributingFactors?: string
  ): Promise<Nonconformance | null> {
    const { data: { user } } = await supabase.auth.getUser()

    return this.updateNonconformance(id, {
      root_cause_analysis: rootCauseAnalysis,
      root_cause_category: rootCauseCategory,
      contributing_factors: contributingFactors,
      investigation_completed_at: new Date().toISOString(),
      investigated_by: user?.id,
      status: 'correction_in_progress',
    })
  },

  /**
   * Record correction (immediate fix)
   */
  async recordCorrection(
    id: string,
    correctionDescription: string
  ): Promise<Nonconformance | null> {
    return this.updateNonconformance(id, {
      correction_description: correctionDescription,
      correction_completed_at: new Date().toISOString(),
      status: 'corrective_action',
    })
  },

  /**
   * Verify correction
   */
  async verifyCorrection(id: string): Promise<Nonconformance | null> {
    const { data: { user } } = await supabase.auth.getUser()

    return this.updateNonconformance(id, {
      correction_verified_by: user?.id,
      correction_verified_at: new Date().toISOString(),
    })
  },

  /**
   * Record corrective action plan
   */
  async recordCorrectiveAction(
    id: string,
    correctiveActionDescription: string,
    dueDate: string,
    effectivenessCriteria?: string,
    effectivenessVerificationDate?: string
  ): Promise<Nonconformance | null> {
    return this.updateNonconformance(id, {
      corrective_action_description: correctiveActionDescription,
      corrective_action_due_date: dueDate,
      effectiveness_criteria: effectivenessCriteria,
      effectiveness_verification_date: effectivenessVerificationDate,
    })
  },

  /**
   * Complete corrective action
   */
  async completeCorrectiveAction(id: string): Promise<Nonconformance | null> {
    return this.updateNonconformance(id, {
      corrective_action_completed_at: new Date().toISOString(),
      status: 'pending_verification',
    })
  },

  /**
   * Verify effectiveness and close NCR
   */
  async verifyEffectiveness(
    id: string,
    isEffective: boolean,
    effectivenessNotes?: string,
    lessonsLearned?: string
  ): Promise<Nonconformance | null> {
    const { data: { user } } = await supabase.auth.getUser()

    if (isEffective) {
      return this.updateNonconformance(id, {
        is_effective: true,
        effectiveness_notes: effectivenessNotes,
        effectiveness_verified_at: new Date().toISOString(),
        effectiveness_verified_by: user?.id,
        lessons_learned: lessonsLearned,
        status: 'closed',
        closed_at: new Date().toISOString(),
        closed_by: user?.id,
      })
    } else {
      // Not effective - reopen for further corrective action
      return this.updateNonconformance(id, {
        is_effective: false,
        effectiveness_notes: effectivenessNotes,
        effectiveness_verified_at: new Date().toISOString(),
        effectiveness_verified_by: user?.id,
        status: 'corrective_action',
      })
    }
  },

  /**
   * Reject NCR (invalid nonconformance)
   */
  async rejectNonconformance(id: string, reason?: string): Promise<Nonconformance | null> {
    const { data: { user } } = await supabase.auth.getUser()

    return this.updateNonconformance(id, {
      status: 'rejected',
      notes: reason,
      closed_at: new Date().toISOString(),
      closed_by: user?.id,
    })
  },

  /**
   * Reopen a closed NCR
   */
  async reopenNonconformance(id: string): Promise<Nonconformance | null> {
    return this.updateNonconformance(id, {
      status: 'open',
      closed_at: null,
      closed_by: null,
      is_effective: null,
      effectiveness_verified_at: null,
      effectiveness_verified_by: null,
    })
  },

  // ============================================
  // STATISTICS
  // ============================================

  /**
   * Get NCR statistics for dashboard
   */
  async getNonconformanceStats(): Promise<NonconformanceStats> {
    const ncrs = await this.getNonconformances()

    const today = new Date()
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

    const byStatus = ncrs.reduce((acc, ncr) => {
      acc[ncr.status] = (acc[ncr.status] || 0) + 1
      return acc
    }, {} as Partial<Record<NcrStatus, number>>)

    const bySeverity = ncrs.reduce((acc, ncr) => {
      acc[ncr.severity] = (acc[ncr.severity] || 0) + 1
      return acc
    }, {} as Partial<Record<NcrSeverity, number>>)

    const bySource = ncrs.reduce((acc, ncr) => {
      acc[ncr.source] = (acc[ncr.source] || 0) + 1
      return acc
    }, {} as Partial<Record<NcrSource, number>>)

    const byCategory = ncrs.reduce((acc, ncr) => {
      acc[ncr.category] = (acc[ncr.category] || 0) + 1
      return acc
    }, {} as Partial<Record<NcrCategory, number>>)

    const openCount = ncrs.filter(
      (n) => !['closed', 'rejected'].includes(n.status)
    ).length

    const todayStr = today.toISOString().split('T')[0]
    const overdueCount = ncrs.filter((n) => {
      if (['closed', 'rejected'].includes(n.status)) return false
      if (!n.corrective_action_due_date) return false
      return n.corrective_action_due_date < todayStr
    }).length

    const pendingVerificationCount = ncrs.filter(
      (n) => n.status === 'pending_verification'
    ).length

    const closedThisMonth = ncrs.filter((n) => {
      if (!n.closed_at) return false
      return new Date(n.closed_at) >= startOfMonth
    }).length

    // Calculate average close time for closed NCRs this year
    const closedNcrs = ncrs.filter((n) => n.status === 'closed' && n.closed_at)
    let averageCloseTime: number | null = null

    if (closedNcrs.length > 0) {
      const totalDays = closedNcrs.reduce((sum, ncr) => {
        const created = new Date(ncr.created_at)
        const closed = new Date(ncr.closed_at!)
        const days = Math.round((closed.getTime() - created.getTime()) / (1000 * 60 * 60 * 24))
        return sum + days
      }, 0)
      averageCloseTime = Math.round(totalDays / closedNcrs.length)
    }

    return {
      total: ncrs.length,
      byStatus,
      bySeverity,
      bySource,
      byCategory,
      openCount,
      overdueCount,
      pendingVerificationCount,
      closedThisMonth,
      averageCloseTime,
    }
  },

  // ============================================
  // NCR COMMENTS
  // ============================================

  /**
   * Get comments for an NCR
   */
  async getComments(ncrId: string): Promise<NcrComment[]> {
    const { data, error } = await supabase
      .from('ncr_comments')
      .select(`
        *,
        profile:profiles!ncr_comments_created_by_fkey(
          id,
          full_name
        )
      `)
      .eq('ncr_id', ncrId)
      .order('created_at', { ascending: true })

    if (error) {
      console.error('Error fetching NCR comments:', error)
      return []
    }

    return (data ?? []) as NcrComment[]
  },

  /**
   * Add a comment to an NCR
   */
  async addComment(comment: NcrCommentInsert): Promise<NcrComment | null> {
    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('ncr_comments')
      .insert({
        ...comment,
        created_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error adding NCR comment:', error)
      throw error
    }

    return data
  },

  /**
   * Delete a comment
   */
  async deleteComment(commentId: string): Promise<boolean> {
    const { error } = await supabase
      .from('ncr_comments')
      .delete()
      .eq('id', commentId)

    if (error) {
      console.error('Error deleting NCR comment:', error)
      throw error
    }

    return true
  },

  // ============================================
  // NCR EVIDENCE
  // ============================================

  /**
   * Get evidence for an NCR
   */
  async getEvidence(ncrId: string): Promise<NcrEvidence[]> {
    const { data, error } = await supabase
      .from('ncr_evidence')
      .select('*')
      .eq('ncr_id', ncrId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching NCR evidence:', error)
      return []
    }

    return data ?? []
  },

  /**
   * Add evidence to an NCR
   */
  async addEvidence(evidence: NcrEvidenceInsert): Promise<NcrEvidence | null> {
    const { data: { user } } = await supabase.auth.getUser()

    const { data, error } = await supabase
      .from('ncr_evidence')
      .insert({
        ...evidence,
        uploaded_by: user?.id,
      })
      .select()
      .single()

    if (error) {
      console.error('Error adding NCR evidence:', error)
      throw error
    }

    return data
  },

  /**
   * Delete evidence
   */
  async deleteEvidence(evidenceId: string): Promise<boolean> {
    const { error } = await supabase
      .from('ncr_evidence')
      .delete()
      .eq('id', evidenceId)

    if (error) {
      console.error('Error deleting NCR evidence:', error)
      throw error
    }

    return true
  },
}
